import { inject, Injectable } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import { TaxMode } from '@df/ui/price/price.service';
import defaultsDeep from '@df/util/fn/defaults-deep';
import { Nexus } from '@tomandco/nexus-api-types';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { IProductConfigurationOptions, ProductConfiguration } from '../product-configuration';
import { GroupedItem } from './grouped-item';
import { ProductConfigurationGroupedItemsService } from './product-configuration-grouped-items.service';

@Injectable()
export class ProductConfigurationGrouped extends ProductConfiguration {
    readonly type = 'grouped';
    readonly groupedItems$ = new BehaviorSubject<GroupedItem[]>([]);
    readonly groupedItemsDataChange$ = new Subject<GroupedItem>();

    protected config?: NProductConfiguration.Grouped.Config;

    private readonly itemsService = inject(ProductConfigurationGroupedItemsService);
    private readonly moduleConfig = inject(NProductConfiguration.OPTIONS);

    override init(
        product: Product<IProductDataV3>,
        options: IProductConfigurationOptions,
        config?: Partial<NProductConfiguration.Grouped.Config>
    ) {
        this.config = defaultsDeep(config || {}, this.moduleConfig.grouped) as NProductConfiguration.Grouped.Config;

        this.product$.pipe(filter((product): product is Product<IProductDataV3> => !!product)).subscribe(product => {
            this.destroyGroupedItems();
            const items = this.itemsService.getItems(product, this.config);
            this.groupedItems$.next(items);
        });

        this.groupedItemsDataChange$.subscribe(() => {
            this.priceChange$.next();
            this.productRequestChange$.next();
        });

        this.groupedItems$
            .pipe(
                filter((items): items is GroupedItem[] => !!items && items.length > 0),
                switchMap(items => merge(...items.map(item => item.change$.pipe(map(() => item)))))
            )
            .subscribe(item => {
                this.groupedItemsDataChange$.next(item);
            });
        super.init(product, options);
    }

    override validate(): NProductConfiguration.Error.Entry[] | undefined {
        const errors = [...(this.itemsService.validate(this.groupedItems$.value, this.config) || []), ...(super.validate() || [])];
        return errors.length ? errors : undefined;
    }

    override toBasketRequest(): Nexus.Lib.Basket.AddToBasketData[] {
        return this.itemsService.getBasketRequests(this.groupedItems$.value);
    }

    override getSelectedProducts(): { product: Product<IProductDataV3>; qty: number }[] {
        return (
            this.groupedItems$.value
                ?.filter(item => item.selected)
                .map(item => ({
                    product: item.product,
                    qty: item.qty
                })) || []
        );
    }

    override price(taxMode?: TaxMode): number {
        return this.rowPrice(taxMode);
    }

    override orgPrice(taxMode?: TaxMode): number | undefined {
        const orgPrice = this.rowOrgPrice(taxMode);
        const price = this.price(taxMode);
        return orgPrice && orgPrice > price ? orgPrice : undefined;
    }

    override ngOnDestroy(): void {
        this.destroyGroupedItems();
        super.ngOnDestroy();
    }

    private destroyGroupedItems(): void {
        this.groupedItems$.value?.forEach(item => item.destroy());
    }
}
